import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import gsApi, { gsApiV3 } from "../auth/auth";
import {
  defaultNotificationSettings,
  getThunkResponse,
  thunkResponse,
  tournamentPackages,
  tournamentRoundsURL,
  tournamentsURL,
  v3TournamentRoundsURL,
  v3TournamentsURL,
  waitlistMember,
} from "../app/api";
import {
  getIDRelationship,
  getOpts,
  getResponse,
  getSerializedData,
  mediumDelay,
} from "../helpers/JSONapi";


import { tournamentFilter } from "../components/events/events-list";
import { getRouting } from "../helpers/Converters";

//replace all occurences of template with the name of your new slice
export const tournamentSlice = createSlice({
  name: "tournaments",
  initialState: {
    tournamentList: [],
    hasMoreTournaments: false,
    tournament: {},
    externalLinks: [],
    tournamentRound: {},
    holeAssignments: [],
    roundCourses: [],
    roundHoles: [],
    roundTees: [],
    tournamentPaymentSettings: {},
    tournamentSponsors: [],
    tournamentSponsorshipTiers: [],
    tournamentPackages: [],
    tournamentPackage: {},
    tournamentAccessCodes: [],
    tournamentTeams: [],
    unassignedPlayers: [],
    tournamentTabs: [],
    tournamentFilter: tournamentFilter,
    notifications: [],
    loading: [],
    accessListCredentials: {
      accessIdentifier: "",
      accessEmail: "",
    },
    waitlistDetails: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      waitListType: "team",
      notes: "",
    },
  },
  //be sure to export any reducers on line 65
  reducers: {
    setWaitlistDetails: (state, action) => {
      state.waitlistDetails = action.payload;
    },
    setWaitlistType: (state, action) => {
      state.waitlistDetails = {
        ...state.waitlistDetails,
        waitListType: action.payload,
      };
    },
    setAccessListCredentials: (state, action) => {
      state.accessListCredentials = action.payload;
    },
    setAccessCodes: (state, action) => {
      state.tournamentAccessCodes = action.payload;
    },
    setTournamentRoundID: (state, action) => {
      const round = state?.tournament?.tournamentRounds?.find?.(
        (r) => r.id === action.payload
      );
      state.tournamentRound = round;
    },
    setTournamentList: (state, action) => {
      state.tournamentList = action.payload;
    },
    setEventListFilter: (state, action) => {
      state.tournamentFilter = action.payload;
    },
    setEventListLocation: (state, action) => {
      state.tournamentFilter = { ...state.tournamentFilter, ...action.payload };
    },
    addNotification: (state, action) => {
      state.notifications.push(action.payload);
    },
    clearNotifications: (state, action) => {
      state.notifications = [];
    },
    updateCurrentPackage: (state, action) => {
      state.tournamentPackage = action.payload;
    },
    updateTournament: (state, action) => {
      state.tournament = { ...state.tournament, ...action.payload };
    },
    clearTournament: (state) => {
      state.tournament = {};
    },
    setHoleAssignments: (state, action) => {
      state.holeAssignments = action.payload
    }
  },
  extraReducers: (builder) => {
    thunkResponse(builder, getPublicTournamentDetails, (state, action) => {
      state.tournament = action.payload;
    });
    thunkResponse(builder, getTournaments, (state, action) => {
      if (action?.meta?.arg?.page === 1) {
        if (action.payload.length === parseInt(action?.meta?.arg?.per_page)) {
          state.hasMoreTournaments = true;
        }
        state.tournamentList = action.payload;
      } else {
        if (action.payload?.length < parseInt(action?.meta?.arg?.per_page)) {
          state.hasMoreTournaments = false;
        }
        state.tournamentList = [...state.tournamentList, ...action.payload];
      }
    });
    thunkResponse(builder, getTournamentPaymentSettings, (state, action) => {
      state.tournamentPaymentSettings = action.payload;
    });
    thunkResponse(builder, getTournamentSponsors, (state, action) => {
      state.tournamentSponsors = action.payload;
    });
    thunkResponse(builder, getTournamentSponsorshipTiers, (state, action) => {
      state.tournamentSponsorshipTiers = action.payload;
    });
    thunkResponse(builder, getTournamentPackages, (state, action) => {
      state.tournamentPackages = action.payload;
    });
    thunkResponse(builder, getTournamentTeams, (state, action) => {
      state.tournamentTeams = action.payload ?? [];
    });
    thunkResponse(builder, getUnassignedPlayers, (state, action) => {
      state.unassignedPlayers = action.payload;
    });
    thunkResponse(builder, getTournamentPackage, (state, action) => {
      state.tournamentPackage = action.payload;
    });
    getThunkResponse(builder, getRoundHoleAssignments, (state, action) => {
      state.holeAssignments = action.payload ?? [];
    });
    getThunkResponse(builder, getRoundCourses, (state, action) => {
      state.roundCourses = action.payload;
    });
    getThunkResponse(builder, getRoundHoles, (state, action) => {
      state.roundHoles = action.payload;
    });
    getThunkResponse(builder, getRoundTees, (state, action) => {
      state.roundTees = action.payload;
    });
    getThunkResponse(builder, getTournamentExternalLinks, (state, action) => {
      state.externalLinks = action.payload;
    });
    thunkResponse(builder, joinWaitList, (state, action) => {}, {
      pending: {
        ...defaultNotificationSettings.pending,
        message: () => "Adding you to the waitlist...",
      },
      fulfilled: {
        ...defaultNotificationSettings.fulfilled,
        message: () => "You have been added to the waitlist.",
      },
      rejected: {
        ...defaultNotificationSettings.rejected,
        timeout: mediumDelay,
        message: () => "Please try again later",
        description: () => "We couldn't add you to the waitlist at this time.",
      },
    });
  },
});

export const getPublicTournamentDetails = createAsyncThunk(
  `${tournamentSlice.name}/getPublicTournamentDetails`,
  async (id, thunkAPI) => {
    const response = await getResponse(gsApi.get, `${tournamentsURL}/${id}`, {}, thunkAPI);
    return response
  }
);

export const getTournaments = createAsyncThunk(
  `${tournamentSlice.name}/getTournaments`,
  async (filter, thunkAPI) => {
    return await getResponse(gsApi.get, tournamentsURL, {
      data: filter,
    });
  }
);

export const getTournamentPaymentSettings = createAsyncThunk(
  `${tournamentSlice.name}/getTournamentPaymentSettings`,
  async (id, thunkAPI) => {
    return await getResponse(
      gsApi.get,
      `${tournamentsURL}/${id}/tournament-payment-setting`
    );
  }
);

export const getTournamentSponsors = createAsyncThunk(
  `${tournamentSlice.name}/getTournamentSponsors`,
  async (id, thunkAPI) => {
    return await getResponse(gsApi.get, `${tournamentsURL}/${id}/sponsors`);
  }
);

export const getTournamentSponsorshipTiers = createAsyncThunk(
  `${tournamentSlice.name}/getTournamentSponsorshipTiers`,
  async (id, thunkAPI) => {
    return await getResponse(gsApi.get, `${tournamentsURL}/${id}/sponsorship-tiers`);
  }
);

export const getTournamentTeams = createAsyncThunk(
  `${tournamentSlice.name}/getTournamentTeams`,
  async (id, thunkAPI) => {
    // v3 registrants view: just what the registrants list shows (names, active players, avatars)
    const teams = await getResponse(
      gsApiV3.get,
      `${v3TournamentsURL}/${id}/tournament-teams`,
      { data: { registrants: true } },
      thunkAPI
    );

    return teams;
  }
);

export const getUnassignedPlayers = createAsyncThunk(
  `${tournamentSlice.name}/getUnassignedPlayers`,
  async (id, thunkAPI) => {
    const teams = await getResponse(
      gsApi.get,
      `${tournamentsURL}/${id}/unassigned-players`
    );

    return teams;
  }
);

export const getTournamentPackages = createAsyncThunk(
  `${tournamentSlice.name}/getTournamentPackages`,
  async (id, thunkAPI) => {
    return await getResponse(
      gsApi.get,
      `${tournamentsURL}/${id}/tournament-packages`
    );
  }
);

export const getTournamentPackage = createAsyncThunk(
  `${tournamentSlice.name}/getTournamentPackage`,
  async (id, thunkAPI) => {
    return await getResponse(gsApi.get, `${tournamentPackages}/${id}`);
  }
);

export const getTournamentRound = createAsyncThunk(
  `${tournamentSlice.name}/getTournamentRound`,
  async (id, thunkAPI) => {
    return await getResponse(gsApi.get, `${tournamentRoundsURL}/${id}`);
  }
);

export const getRoundHoleAssignments = createAsyncThunk(
  `${tournamentSlice.name}/getRoundHoleAssignments`,
  async (roundID, thunkAPI) => {
    // v3 public view: each spot's openings and the teams and players starting there
    return await getResponse(
      gsApiV3.get,
      getRouting(v3TournamentRoundsURL, [roundID, "round-hole-assignments"]),
      { data: { public: true } },
      thunkAPI
    );
  }
);

export const getRoundCourses = createAsyncThunk(
  `${tournamentSlice.name}/getRoundCourses`,
  async (roundID, thunkAPI) => {
    return await getResponse(
      gsApi.get,
      getRouting(tournamentRoundsURL, [roundID, "round-courses"])
    );
  }
);
export const getRoundHoles = createAsyncThunk(
  `${tournamentSlice.name}/getRoundHoles`,
  async (roundID, thunkAPI) => {
    return await getResponse(
      gsApi.get,
      getRouting(tournamentRoundsURL, [roundID, "round-holes"])
    );
  }
);
export const getRoundTees = createAsyncThunk(
  `${tournamentSlice.name}/getRoundTees`,
  async (roundID, thunkAPI) => {
    return await getResponse(
      gsApi.get,
      getRouting(tournamentRoundsURL, [roundID, "round-tees"])
    );
  }
);

export const getTournamentExternalLinks = createAsyncThunk(
  "tournaments/getTournamentExternalLinks",
  async (id, thunkAPI) => {
    let url = `${tournamentsURL}/${id}/external-links`;
    const response = await gsApi.get(url);
    return response;
  }
);

export const getTournamentAccessCode = createAsyncThunk(
  "tournaments/getTournamentAccessCode",
  async (filter, thunkAPI) => {
    const { tournamentID, accessIdentifier, accessEmail } = filter;
    const params = {
      access_identifier: accessIdentifier,
      access_email: accessEmail,
    };
    let response = await getResponse(
      gsApi.get,
      getRouting(tournamentsURL, [tournamentID, "access-group-member-search"]),
      { data: params },
      thunkAPI
    );
    if (response?.length > 0) {
      response = response.map?.((r) => {
        return { ...r, credentials: filter };
      });
    }
    return response;
  }
);

export const joinWaitList = createAsyncThunk(
  `${tournamentSlice.name}/joinWaitList`,
  async (waitListMember, thunkAPI) => {
    let serializedData = getSerializedData(
      waitListMember,
      "wait-list-members",
      getOpts(waitListMember, [...getIDRelationship("tournament")])
    );

    const response = await getResponse(
      gsApi.post,
      waitlistMember,
      serializedData,
      thunkAPI
    );

    return response;
  }
);

export const {
  setWaitlistDetails,
  setAccessListCredentials,
  setAccessCodes,
  setTournamentList,
  setEventListFilter,
  addNotification,
  clearNotifications,
  updateCurrentPackage,
  setEventListLocation,
  setTournamentRoundID,
  updateTournament,
  setWaitlistType,
  clearTournament,
  setHoleAssignments
} = tournamentSlice.actions;

//export any selectors needed

export const selectTournamentRound = (state) =>
  state?.[tournamentSlice.name]?.tournamentRound;

export const selectNotifications = (state) =>
  state?.[tournamentSlice.name]?.notifications;

export const selectLoading = (state) => state?.[tournamentSlice.name]?.loading;

export const selectPublicTournament = (state) =>
  state?.[tournamentSlice.name]?.tournament;

export const selectTournamentTabs = (state) =>
  state?.[tournamentSlice.name]?.tournamentTabs;

export const selectTournaments = (state) =>
  state?.[tournamentSlice.name]?.tournamentList;

export const selectTournamentPaymentSettings = (state) =>
  state?.[tournamentSlice.name]?.tournamentPaymentSettings;

export const selectTournamentSponsors = (state) =>
  state?.[tournamentSlice.name]?.tournamentSponsors;

export const selectTournamentSponsorshipTiers = (state) =>
  state?.[tournamentSlice.name]?.tournamentSponsorshipTiers;

export const selectTournamentTeams = (state) =>
  state?.[tournamentSlice.name]?.tournamentTeams;

export const selectTournamentPackages = (state) =>
  state?.[tournamentSlice.name]?.tournamentPackages;

export const selectTournamentPackage = (state) =>
  state?.[tournamentSlice.name]?.tournamentPackage;

export const selectTournamentFilter = (state) =>
  state?.[tournamentSlice.name]?.tournamentFilter;

export const selectHoleAssignments = (state) =>
  state?.[tournamentSlice.name]?.holeAssignments;

export const selectRoundCourses = (state) =>
  state?.[tournamentSlice.name]?.roundCourses;

export const selectRoundHoles = (state) =>
  state?.[tournamentSlice.name]?.roundHoles;

export const selectRoundTees = (state) =>
  state?.[tournamentSlice.name]?.roundTees;

export const selectHasMoreTournaments = (state) =>
  state?.[tournamentSlice.name]?.hasMoreTournaments;

export const selectExternalLinks = (state) =>
  state?.[tournamentSlice.name]?.externalLinks;

export const selectTournamentAccessCodes = (state) =>
  state?.[tournamentSlice.name]?.tournamentAccessCodes;

export const selectAccessListCredentials = (state) =>
  state?.[tournamentSlice.name]?.accessListCredentials;

export const selectWaitlistDetails = (state) =>
  state?.[tournamentSlice.name]?.waitlistDetails;

export const selectUnassignedPlayers = (state) =>
  state?.[tournamentSlice?.name]?.unassignedPlayers;

export default tournamentSlice.reducer;

//ADD reducer to store.js
